// Copyright (c) 2024 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * Step 1: Verify details step component.
 *
 * Contains three sub-steps:
 * 1. Verify ledger account configurations are complete
 * 2. Confirm operational data is ready
 * 3. Review and resolve asset exceptions
 */
import { ledgerAccountTypeSchema } from '@/account/api/types';
import { useOrganizationLedgerAccounts } from '@/account/hooks/use-organization-ledger-accounts';
import {
  calculateCloseRunTimeRange,
  formatTimeRange,
} from '@/accounting/utilities/estimate-close-run-time';
import { useCurrentUser } from '@/authentication/hooks/use-current-user';
import {
  Autorenew,
  CheckCircleOutlineRounded,
  ChevronRightRounded,
  ErrorOutlineRounded,
  OpenInNewRounded,
  WarningAmberOutlined,
} from '@/constants/hardfin-standard-icons';
import { ExceptionSummaryCard } from '@/report/components/exception-summary-card';
import { useDateInconsistencies } from '@/report/hooks/use-date-inconsistencies';
import { useDetailInconsistencies } from '@/report/hooks/use-detail-inconsistencies';
import { pluralize } from '@/utilities/pluralize';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Checkbox,
  Chip,
  Divider,
  FormControlLabel,
  LinearProgress,
  Link,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { useEffect, useMemo, useState } from 'react';

interface Props {
  onNext: () => void;
  isCreatingCloseRun?: boolean;
  isPeriodLocked?: boolean;
  isPeriodNotCompleted?: boolean;
  /**
   * Whether a close run has already been created for this period.
   * When true, the step displays in a locked read-only state with a simple
   * "Next" button instead of requiring the user to recreate the close run.
   */
  hasExistingCloseRun?: boolean;
}

// Get all ledger account types from the schema
const allLedgerAccountTypes = ledgerAccountTypeSchema.options;

export function VerifyDetailsStep(props: Props) {
  const [operationalDataConfirmed, setOperationalDataConfirmed] =
    useState(false);

  // Track animation completion state for each exception summary card
  const [isDateAnimationComplete, setIsDateAnimationComplete] = useState(false);
  const [isDetailAnimationComplete, setIsDetailAnimationComplete] =
    useState(false);

  /**
   * Handles the completion of the detail exceptions animation.
   * Sets the animation complete flag to true so the UI knows data is ready.
   */
  function handleDetailAnimationComplete() {
    setIsDetailAnimationComplete(true);
  }

  // Get current user's organization
  const { user } = useCurrentUser();
  const orgKey = user.organization.key;

  // Fetch real ledger account configuration
  const { data: ledgerAccountsData } = useOrganizationLedgerAccounts(orgKey);

  // Calculate ledger account configuration status from real data
  const configuredCount = allLedgerAccountTypes.filter((accountType) => {
    const value = ledgerAccountsData.ledgerAccounts[accountType];
    return value !== null && value !== '' && value !== undefined;
  }).length;
  const totalAccounts = allLedgerAccountTypes.length;
  const ledgerAccountsComplete = configuredCount === totalAccounts;
  const configurationProgress = (configuredCount / totalAccounts) * 100;

  // Fetch real exception data
  const { data: dateInconsistencies, isLoading: isLoadingDate } =
    useDateInconsistencies();
  const { data: detailInconsistencies, isLoading: isLoadingDetail } =
    useDetailInconsistencies();

  // Reset animation completion state when data starts loading
  useEffect(() => {
    if (isLoadingDate) {
      setIsDateAnimationComplete(false);
    }
  }, [isLoadingDate]);

  useEffect(() => {
    if (isLoadingDetail) {
      setIsDetailAnimationComplete(false);
    }
  }, [isLoadingDetail]);

  // Determine if exceptions data is still loading (includes animation state)
  const isLoadingExceptions = isLoadingDate || isLoadingDetail;
  const isAnimatingExceptions =
    !isDateAnimationComplete || !isDetailAnimationComplete;

  // Calculate exception counts from real data
  const dateExceptionCount = dateInconsistencies?.assets.length ?? 0;
  const detailExceptionCount = detailInconsistencies?.assets.length ?? 0;
  const totalExceptions = dateExceptionCount + detailExceptionCount;

  // Only consider exceptions resolved when:
  // 1. Data has loaded
  // 2. Animations have completed
  // 3. There are no exceptions
  const hasExceptions =
    !isLoadingExceptions && !isAnimatingExceptions && totalExceptions > 0;
  const exceptionsResolved =
    !isLoadingExceptions && !isAnimatingExceptions && totalExceptions === 0;

  // Determine if user can proceed
  const canProceed =
    ledgerAccountsComplete && operationalDataConfirmed && exceptionsResolved;

  // Calculate estimated time range based on asset count from the inconsistencies data.
  // Both date and detail inconsistency reports analyze all assets in the organization,
  // so totalAnalyzed represents the total asset count.
  const estimatedTimeRange = useMemo(() => {
    const assetCount = dateInconsistencies?.totalAnalyzed ?? 0;
    return calculateCloseRunTimeRange(assetCount);
  }, [dateInconsistencies?.totalAnalyzed]);

  const formattedEstimatedTime = useMemo(() => {
    return formatTimeRange(estimatedTimeRange);
  }, [estimatedTimeRange]);

  // When a close run already exists, the step is locked and user can simply proceed
  if (props.hasExistingCloseRun) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Alert severity="info">
          <AlertTitle>Close run already created</AlertTitle>A close run has
          already been created for this period. You can proceed to review the
          rollforward data.
        </Alert>

        {/* Navigation button - bottom */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            marginTop: 1,
            marginBottom: 3,
          }}
        >
          <Button
            variant="contained"
            color="secondary"
            size="small"
            endIcon={<ChevronRightRounded fontSize="small" />}
            onClick={props.onNext}
          >
            Next
          </Button>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {/* Warning alert for period not yet completed */}
      {props.isPeriodNotCompleted && (
        <Alert severity="warning">
          <AlertTitle>Period not yet completed</AlertTitle>
          This period has not yet ended. You can only close periods that have
          fully completed.
        </Alert>
      )}

      {/* Sub-step 1: Ledger Account Configuration */}
      <Card
        variant="outlined"
        sx={
          ledgerAccountsComplete
            ? {
                borderColor: 'success.light',
                borderWidth: 2,
              }
            : {
                borderColor: 'warning.light',
                borderWidth: 1,
              }
        }
      >
        <CardHeader
          title="Ledger account configuration"
          subheader="Verify that all ledger accounts are configured with valid GL numbers and names for proper journal entry generation"
          sx={{ paddingBottom: 0 }}
          avatar={
            ledgerAccountsComplete ? (
              <CheckCircleOutlineRounded color="success" fontSize="small" />
            ) : (
              <WarningAmberOutlined color="warning" fontSize="small" />
            )
          }
          action={
            <Button
              variant="outlined"
              size="small"
              endIcon={<OpenInNewRounded fontSize="small" />}
              component={Link}
              href="/organization#ledger-accounts"
              target="_blank"
              rel="noopener"
            >
              Configure ledger accounts
            </Button>
          }
        />
        <CardContent sx={{ padding: 3 }}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="flex-start"
          >
            <Box sx={{ flex: 1 }}>
              <Box sx={{ mb: 2 }}>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  sx={{ mb: 1 }}
                >
                  <Typography variant="body1">
                    {configuredCount} of {totalAccounts} accounts configured
                  </Typography>
                  <Chip
                    label={ledgerAccountsComplete ? 'Complete' : 'Incomplete'}
                    color={ledgerAccountsComplete ? 'success' : 'warning'}
                    size="small"
                  />
                </Stack>
                <LinearProgress
                  variant="determinate"
                  value={configurationProgress}
                  color={ledgerAccountsComplete ? 'success' : 'warning'}
                  sx={{ height: 8, borderRadius: 1 }}
                />
              </Box>

              {!ledgerAccountsComplete && (
                <Alert severity="warning">
                  Some ledger accounts are missing configuration. Please
                  configure all accounts before proceeding.
                </Alert>
              )}
            </Box>
          </Stack>
        </CardContent>
      </Card>

      {/* Sub-step 2: Asset Exceptions Review */}
      <Card
        variant="outlined"
        sx={
          isLoadingExceptions || isAnimatingExceptions
            ? {
                borderColor: 'info.light',
                borderWidth: 1,
              }
            : exceptionsResolved
              ? {
                  borderColor: 'success.light',
                  borderWidth: 2,
                }
              : {
                  borderColor: 'error.light',
                  borderWidth: 1,
                }
        }
      >
        <CardHeader
          title="Asset exceptions"
          subheader="Review and resolve any asset exceptions before proceeding with the accounting close"
          sx={{ paddingBottom: 0 }}
          avatar={
            isLoadingExceptions || isAnimatingExceptions ? (
              <Autorenew
                color="info"
                fontSize="small"
                sx={{
                  animation: 'spin 2s linear infinite',
                  '@keyframes spin': {
                    '0%': { transform: 'rotate(0deg)' },
                    '100%': { transform: 'rotate(360deg)' },
                  },
                }}
              />
            ) : exceptionsResolved ? (
              <CheckCircleOutlineRounded color="success" fontSize="small" />
            ) : (
              <ErrorOutlineRounded color="error" fontSize="small" />
            )
          }
        />
        <CardContent sx={{ paddingX: 3 }}>
          {(isLoadingExceptions || isAnimatingExceptions) && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Loading exception data...
            </Alert>
          )}

          {hasExceptions && (
            <Alert severity="info" sx={{ mb: 2 }}>
              {pluralize(totalExceptions, { singular: 'asset' })} require
              attention. Please resolve all exceptions before proceeding.
            </Alert>
          )}

          {exceptionsResolved && (
            <Alert severity="success" sx={{ mb: 2 }}>
              No asset exceptions require attention.
            </Alert>
          )}

          {/* Date Exceptions Summary */}
          <Box sx={{ mb: 2 }}>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              sx={{ mb: 1 }}
            >
              <Stack direction="column">
                <Typography variant="subtitle1">Date exceptions</Typography>
                <Typography
                  variant="body1"
                  color="text.secondary"
                  sx={{ mb: 1.5 }}
                >
                  Assets with date inconsistencies such as in-service dates
                  before inventory dates, movements before inventory, etc.
                </Typography>
              </Stack>
              {dateExceptionCount > 0 && (
                <Button
                  variant="outlined"
                  size="small"
                  endIcon={<OpenInNewRounded fontSize="small" />}
                  component={Link}
                  href="/reports/exception/asset/date"
                  target="_blank"
                  rel="noopener"
                >
                  Review date exceptions
                </Button>
              )}
            </Stack>
            <ExceptionSummaryCard
              stats={{
                totalAnalyzed: dateInconsistencies?.totalAnalyzed ?? 0,
                withExceptions: dateExceptionCount,
              }}
              isLoading={isLoadingDate}
              onAnimationComplete={() => setIsDateAnimationComplete(true)}
            />
          </Box>

          <Divider sx={{ my: 2 }} />

          {/* Detail Exceptions Summary */}
          <Box>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              sx={{ mb: 1 }}
            >
              <Stack direction="column">
                <Typography variant="subtitle1">Detail exceptions</Typography>
                <Typography
                  variant="body1"
                  color="text.secondary"
                  sx={{ mb: 1.5 }}
                >
                  Assets with detail inconsistencies such as missing cost basis,
                  salvage value issues, missing useful life, etc.
                </Typography>
              </Stack>
              {detailExceptionCount > 0 && (
                <Button
                  variant="outlined"
                  size="small"
                  endIcon={<OpenInNewRounded fontSize="small" />}
                  component={Link}
                  href="/reports/exception/asset/detail"
                  target="_blank"
                  rel="noopener"
                >
                  Review detail exceptions
                </Button>
              )}
            </Stack>

            <ExceptionSummaryCard
              stats={{
                totalAnalyzed: detailInconsistencies?.totalAnalyzed ?? 0,
                withExceptions: detailExceptionCount,
              }}
              isLoading={isLoadingDetail}
              onAnimationComplete={handleDetailAnimationComplete}
            />
          </Box>
        </CardContent>
      </Card>

      {/* Sub-step 3: Operational Data Confirmation */}
      <Card
        variant="outlined"
        sx={
          operationalDataConfirmed
            ? {
                borderColor: 'success.light',
                borderWidth: 2,
              }
            : undefined
        }
      >
        <CardHeader
          title="Operational data confirmation"
          subheader="Confirm that all operational data—such as purchase orders, fulfillment orders, shipments, sales records, and asset scrap—have been updated for the period"
          sx={{ paddingBottom: 0 }}
          avatar={
            operationalDataConfirmed ? (
              <CheckCircleOutlineRounded color="success" fontSize="small" />
            ) : (
              <ErrorOutlineRounded color="disabled" fontSize="small" />
            )
          }
        />
        <CardContent sx={{ paddingX: 3 }}>
          <Box sx={{ mx: 2 }}>
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={operationalDataConfirmed}
                  onChange={(e) =>
                    setOperationalDataConfirmed(e.target.checked)
                  }
                />
              }
              label={
                <Typography variant="body1">
                  The operations team has confirmed that all data is entered and
                  the system is ready for accounting close.
                </Typography>
              }
            />
          </Box>
        </CardContent>
      </Card>

      {/* Continue button - bottom */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          alignItems: 'center',
          gap: 2,
          marginTop: 1,
          marginBottom: 3,
        }}
      >
        {/* Estimated time display */}
        {!isLoadingDate && dateInconsistencies && !props.isCreatingCloseRun && (
          <Typography variant="body2" color="text.secondary">
            Estimated processing time: {formattedEstimatedTime}
          </Typography>
        )}
        <Tooltip
          arrow
          title={
            props.isPeriodLocked
              ? 'This period is already locked'
              : props.isPeriodNotCompleted
                ? 'This period has not yet ended'
                : !canProceed
                  ? 'Please complete all steps above before proceeding'
                  : ''
          }
        >
          <Box>
            <Button
              variant="contained"
              color="secondary"
              size="small"
              endIcon={<ChevronRightRounded fontSize="small" />}
              onClick={props.onNext}
              disabled={
                !canProceed ||
                props.isCreatingCloseRun ||
                props.isPeriodLocked ||
                props.isPeriodNotCompleted
              }
            >
              {props.isCreatingCloseRun
                ? 'Creating close run...'
                : 'Begin with inventory rollforward'}
            </Button>
          </Box>
        </Tooltip>
      </Box>
    </Box>
  );
}
